import { AfterViewInit, Component, ElementRef, Input, OnChanges, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Conversation, Message } from '../../shared/models';
import { ChatStoreService } from '../../shared/chat-store.service';

@Component({
  standalone: true,
  selector: 'app-chat-body',
  imports: [CommonModule],
  template: `
  <section class="body" aria-label="Chat messages">
    <ng-container *ngFor="let m of messages; let i = index">
      <div class="day" *ngIf="isNewDay(i)">{{ dayLabel(m.timestamp) }}</div>

      <div class="msg" [class.out]="isOutgoing(m)" #messageRow>
        <div class="bubble" [class.mediaBubble]="isMediaType(m.type)" [class.richBubble]="isRichType(m.type)">
          <ng-container [ngSwitch]="m.type">
            <div *ngSwitchCase="'text'" class="text" [innerHTML]="highlightText(m.content)"></div>

            <div *ngSwitchCase="'interactive'" class="interactiveCard">
              <div class="text interactivePrompt" *ngIf="m.content" [innerHTML]="highlightText(m.content)"></div>
              <div class="interactiveButtons" *ngIf="m.interactive?.buttons?.length">
                <button class="waBtn" type="button" *ngFor="let btn of m.interactive?.buttons">
                  {{ btn.title }}
                </button>
              </div>
            </div>

            <div *ngSwitchCase="'interactive_reply'" class="interactiveReply">
              <div class="replyHead">Selected response</div>
              <div class="waBtn replyBtn" [innerHTML]="highlightText(m.interactive?.title || m.content)"></div>
            </div>

            <div *ngSwitchCase="'flow'" class="flowCard">
              <div class="flowHdr" *ngIf="m.interactive?.headerText" [innerHTML]="highlightText(m.interactive?.headerText || '')"></div>
              <div class="text flowBody" [innerHTML]="highlightText(m.content || m.interactive?.bodyText || '')"></div>
              <div class="flowFooter" *ngIf="m.interactive?.footerText" [innerHTML]="highlightText(m.interactive?.footerText || '')"></div>
              <div class="flowAction" *ngIf="m.interactive?.ctaText">
                <button class="waBtn flowBtn" type="button">{{ m.interactive?.ctaText }}</button>
              </div>
            </div>

            <div *ngSwitchCase="'flow_response'" class="flowResponseCard">
              <div class="flowResIcon">🧾</div>
              <div class="flowResText">
                <div class="flowResTitle" [innerHTML]="highlightText(flowResponseTitle(m))"></div>
                <div class="flowResSub" [innerHTML]="highlightText(m.interactive?.responseBody || 'Response sent')"></div>
              </div>
            </div>

            <div *ngSwitchCase="'image'" class="media">
              <img class="mediaImage" *ngIf="m.media?.url" [src]="m.media?.url || ''" alt="Shared image" />
              <div class="mediaFallback" *ngIf="!m.media?.url">{{ mediaIcon(m.type) }} IMAGE</div>
              <div class="caption" *ngIf="m.content && m.content.toUpperCase() !== 'IMAGE'" [innerHTML]="highlightText(m.content)"></div>
            </div>

            <div *ngSwitchCase="'video'" class="media">
              <div class="videoShell" *ngIf="m.media?.url">
                <video class="mediaVideo" [src]="m.media?.url || ''" controls preload="metadata"></video>
              </div>
              <div class="mediaFallback" *ngIf="!m.media?.url">{{ mediaIcon(m.type) }} VIDEO</div>
              <div class="caption" *ngIf="m.content && m.content.toUpperCase() !== 'VIDEO'" [innerHTML]="highlightText(m.content)"></div>
            </div>

            <div *ngSwitchCase="'audio'" class="media">
              <audio class="mediaAudio" *ngIf="m.media?.url" [src]="m.media?.url || ''" controls></audio>
              <div class="mediaFallback" *ngIf="!m.media?.url">{{ mediaIcon(m.type) }} AUDIO</div>
            </div>

            <div *ngSwitchCase="'document'" class="media">
              <a class="docCard" *ngIf="m.media?.url" [href]="m.media?.url || ''" target="_blank" rel="noopener">
                <span class="docIcon">DOC</span>
                <span class="docText">{{ m.content || 'Open document' }}</span>
              </a>
              <div class="mediaFallback" *ngIf="!m.media?.url">{{ mediaIcon(m.type) }} DOCUMENT</div>
            </div>

            <div *ngSwitchDefault class="mediaFallback">
              {{ mediaIcon(m.type) }} {{ (m.rawType || m.type).toUpperCase() }}
            </div>
          </ng-container>

          <a
            class="linkPreview"
            *ngIf="m.type==='text' && previewUrl(m.content) as pUrl"
            [href]="pUrl"
            target="_blank"
            rel="noopener">
            <div class="lpUrl">{{ pUrl }}</div>
          </a>

          <div class="meta">
            <span class="time">{{ store.hhmm(m.timestamp) }}</span>
            <span
              class="ticks"
              *ngIf="isOutgoing(m)"
              [class.read]="m.status==='read'"
              [class.failed]="m.status==='failed'"
              [class.single]="m.status==='sent'"
              [class.double]="m.status==='delivered' || m.status==='read'"
              [class.sending]="m.status==='sending'">
              <ng-container [ngSwitch]="m.status">
                <span *ngSwitchCase="'sending'" class="statusGlyph">◷</span>
                <span *ngSwitchCase="'failed'" class="statusGlyph">!</span>

                <svg *ngSwitchCase="'sent'" class="tickSvg" viewBox="0 0 12 10" aria-hidden="true">
                  <path d="M1.2 5.6l2.3 2.5 6-6.2"></path>
                </svg>

                <ng-container *ngSwitchDefault>
                  <svg class="tickSvg twin first" viewBox="0 0 12 10" aria-hidden="true">
                    <path d="M1.2 5.6l2.3 2.5 6-6.2"></path>
                  </svg>
                  <svg class="tickSvg twin second" viewBox="0 0 12 10" aria-hidden="true">
                    <path d="M1.2 5.6l2.3 2.5 6-6.2"></path>
                  </svg>
                </ng-container>
              </ng-container>
            </span>
          </div>
        </div>
      </div>
    </ng-container>
  </section>
  `,
  styles: [`
    :host{display:block;height:100%;min-height:0}
    .body{height:100%;min-height:0;overflow:auto;padding:16px 16px 10px;background:
      radial-gradient(900px 380px at 10% 0%, rgba(0,168,132,.10), transparent 60%),
      radial-gradient(900px 420px at 100% 0%, rgba(0,168,132,.08), transparent 55%),
      var(--chatBg)}
    .day{position:sticky;top:0;width:fit-content;margin:0 auto 14px;padding:6px 10px;border-radius:999px;font-size:12px;color:var(--muted);border:1px solid var(--border);background:var(--panel)}
    .msg{display:flex;margin-bottom:10px}
    .msg.out{justify-content:flex-end}
    .bubble{
      max-width:min(690px, 86%);
      padding:9px 11px 6px;
      border-radius:12px;
      background:linear-gradient(180deg, rgba(255,255,255,.015), rgba(255,255,255,0)), var(--bubbleIn);
      border:1px solid rgba(255,255,255,.07);
      box-shadow:0 2px 8px rgba(0,0,0,.14);
    }
    .msg.out .bubble{
      background:linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,0)), var(--bubbleOut);
      border-color:rgba(0,168,132,.28)
    }
    .mediaBubble{padding:6px 6px 4px;overflow:hidden}
    .richBubble{padding:8px 10px 6px}
    .text{white-space:pre-wrap;line-height:1.35}
    .interactiveCard{display:flex;flex-direction:column;gap:8px}
    .interactivePrompt{
      min-width:260px;
      max-width:360px;
      margin:0 auto;
    }
    .interactiveButtons{
      display:flex;
      flex-direction:column;
      gap:2px;
      margin-top:2px;
      border-top:1px solid rgba(134,150,160,.24);
      padding-top:6px
    }
    .waBtn{
      width:100%;
      min-width:260px;
      max-width:360px;
      border:0;
      background:rgba(17,27,33,.82);
      color:#25d366;
      border-radius:8px;
      padding:9px 12px;
      font-size:15px;
      font-weight:600;
      text-align:left;
      cursor:pointer;
      text-align:center;
      min-height:44px;
      line-height:1.2;
      border:1px solid rgba(255,255,255,.07);
      margin:0 auto;
    }
    .waBtn:hover{background:rgba(17,27,33,.95)}
    :host-context(body.light) .waBtn{
      background:#f1f5f9;
      color:#16a34a;
      border:1px solid rgba(148,163,184,.35)
    }
    .interactiveReply{
      display:flex;
      flex-direction:column;
      gap:6px;
      border-top:1px solid rgba(134,150,160,.24);
      padding-top:6px;
    }
    .replyHead{
      font-size:11px;
      text-transform:uppercase;
      letter-spacing:.25px;
      opacity:.78;
      font-weight:700;
    }
    .replyBtn{
      cursor:default;
      pointer-events:none;
      margin:0;
    }
    .flowCard{
      border:1px solid rgba(255,255,255,.10);
      background:rgba(17,27,33,.62);
      border-radius:12px;
      padding:10px 10px 0;
    }
    .flowHdr{font-weight:900;line-height:1.25;font-size:16px;margin-bottom:4px}
    .flowBody{font-size:14px;font-weight:500;line-height:1.35}
    .flowFooter{margin-top:6px;font-size:12px;opacity:.72;padding-bottom:8px}
    .flowAction{
      margin-top:6px;
      margin-left:-10px;
      margin-right:-10px;
      border-top:1px solid rgba(255,255,255,.10);
      padding:8px 10px 10px;
    }
    .flowBtn{
      min-width:100%;
      max-width:100%;
      border-radius:9px;
      color:rgba(255,255,255,.62);
      font-size:13px;
      font-weight:700;
      background:rgba(255,255,255,.05);
      border-color:rgba(255,255,255,.08);
    }
    .flowResponseCard{
      display:flex;
      gap:10px;
      align-items:center;
      border:1px solid rgba(22,163,74,.35);
      background:rgba(22,163,74,.20);
      border-radius:12px;
      padding:10px;
      min-width:260px;
      max-width:360px;
      margin:0 auto;
    }
    .flowResIcon{
      width:36px;
      height:36px;
      border-radius:999px;
      display:flex;
      align-items:center;
      justify-content:center;
      background:rgba(0,0,0,.28);
      font-size:17px;
    }
    .flowResText{display:flex;flex-direction:column;gap:2px;min-width:0}
    .flowResTitle{font-size:16px;font-weight:800;line-height:1.2}
    .flowResSub{font-size:13px;opacity:.88}
    .media{display:flex;flex-direction:column;gap:8px}
    .mediaImage{
      width:min(360px, 100%);
      max-height:340px;
      object-fit:cover;
      border-radius:10px;
      border:1px solid rgba(255,255,255,.08);
      background:#000;
      box-shadow:0 10px 20px rgba(0,0,0,.25);
    }
    .videoShell{
      width:min(360px, 100%);
      border-radius:12px;
      overflow:hidden;
      background:#0b141a;
      border:1px solid rgba(255,255,255,.08);
      box-shadow:inset 0 -48px 90px rgba(0,0,0,.35), 0 10px 20px rgba(0,0,0,.28)
    }
    .mediaVideo{
      width:100%;
      max-height:360px;
      display:block;
      background:#000;
      border:0;
      border-radius:0;
    }
    .mediaAudio{width:min(360px, 100%)}
    .caption{
      font-size:14px;
      line-height:1.35;
      padding:0 4px 2px;
      color:rgba(241,245,249,.95);
    }
    :host-context(body.light) .caption{color:#0f172a}
    .docCard{
      display:flex;
      align-items:center;
      gap:10px;
      text-decoration:none;
      color:inherit;
      border:1px solid rgba(255,255,255,.10);
      border-radius:10px;
      padding:10px;
      background:rgba(11,20,26,.58);
    }
    .docIcon{
      display:inline-flex;align-items:center;justify-content:center;
      width:36px;height:36px;border-radius:9px;font-size:10px;font-weight:900;
      background:rgba(37,99,235,.20);border:1px solid rgba(59,130,246,.36)
    }
    .docText{font-size:13px;line-height:1.3;word-break:break-word;color:rgba(241,245,249,.94)}
    :host-context(body.light) .docText{color:#0f172a}
    .mediaFallback{
      display:inline-flex;align-items:center;gap:8px;font-weight:800;font-size:12px;
      color:var(--muted);padding:7px 9px;border:1px solid var(--border);border-radius:10px
    }
    .linkPreview{
      display:block;
      margin-top:8px;
      border:1px solid rgba(255,255,255,.12);
      border-radius:10px;
      background:rgba(17,27,33,.62);
      padding:8px 10px;
      text-decoration:none;
      color:inherit;
    }
    .lpUrl{
      color:#22c55e;
      font-size:14px;
      line-height:1.35;
      word-break:break-all;
    }
    :host-context(body.light) .linkPreview{
      background:#f8fafc;
      border-color:rgba(148,163,184,.35);
    }
    :host-context(body.light) .lpUrl{color:#15803d}
    .meta{
      margin-top:6px;
      display:flex;
      justify-content:flex-end;
      gap:6px;
      color:var(--muted);
      font-size:11px;
      align-items:center;
      opacity:.9;
    }
    body.light .meta{color:rgba(11,20,26,.55)}
    .ticks{
      display:inline-flex;
      align-items:center;
      justify-content:center;
      min-width:18px;
      height:12px;
      line-height:1;
      color:#a7b3bb;
      position:relative;
    }
    .statusGlyph{font-size:11px;font-weight:900;line-height:1}
    .ticks.sending{color:#94a3b8}
    .tickSvg{
      width:12px;
      height:10px;
      stroke:currentColor;
      fill:none;
      stroke-width:1.8;
      stroke-linecap:round;
      stroke-linejoin:round;
    }
    .ticks.double{width:18px}
    .ticks.double .twin{
      position:absolute;
      top:1px;
    }
    .ticks.double .twin.first{left:1px}
    .ticks.double .twin.second{left:6px}
    .ticks.read{color:#53bdeb}
    .ticks.failed{
      min-width:16px;
      width:16px;
      height:16px;
      border-radius:999px;
      background:#e11d48;
      color:#fff;
      font-size:12px;
      font-weight:900;
      letter-spacing:0;
      box-shadow:0 0 0 1px rgba(255,255,255,.18), 0 2px 6px rgba(225,29,72,.35);
    }
    @media (max-width: 900px){ .bubble{max-width:92%} }
  `]
})
export class ChatBodyComponent implements AfterViewInit, OnChanges {
  @Input() conversation!: Conversation;
  @Input() messages: Message[] = [];
  @Input() searchQuery = '';
  @ViewChildren('messageRow') messageRows!: QueryList<ElementRef<HTMLElement>>;

  constructor(public store: ChatStoreService) {}

  ngAfterViewInit(): void {
    this.jumpToFirstOccurrence();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchQuery'] || changes['messages']) {
      setTimeout(() => this.jumpToFirstOccurrence(), 0);
    }
  }

  isOutgoing(m: Message): boolean {
    if (m.type === 'flow_response') return true;
    return m.from === this.conversation.to;
  }

  isNewDay(index: number): boolean {
    if (index === 0) return true;
    const prev = this.messages[index - 1];
    const cur = this.messages[index];
    if (!prev || !cur) return false;
    return this.dayKey(prev.timestamp) !== this.dayKey(cur.timestamp);
  }

  dayLabel(ts: string): string {
    const d = new Date(ts);
    const now = new Date();
    if (this.dayKey(ts) === this.dayKey(now.toISOString())) return 'Today';

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (this.dayKey(ts) === this.dayKey(yesterday.toISOString())) return 'Yesterday';

    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }

  private dayKey(ts: string): string {
    const d = new Date(ts);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }

  mediaIcon(t: string): string {
    if (t === 'image') return '🖼️';
    if (t === 'video') return '🎬';
    if (t === 'audio') return '🎧';
    return '📄';
  }

  previewUrl(text: string): string | null {
    const match = text.match(/https?:\/\/[^\s]+/i);
    return match?.[0] || null;
  }

  isMediaType(t: Message['type']): boolean {
    return t === 'image' || t === 'video' || t === 'audio' || t === 'document';
  }

  isRichType(t: Message['type']): boolean {
    return t === 'interactive' || t === 'interactive_reply' || t === 'flow';
  }

  flowResponseTitle(m: Message): string {
    return m.content && !m.content.startsWith('[') ? m.content : 'Flow response';
  }

  highlightText(value: string): string {
    const text = String(value || '');
    const escapedText = this.escapeHtml(text);
    const query = this.searchQuery.trim();
    if (!query) return escapedText;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(${escapedQuery})`, 'gi');
    return escapedText.replace(
      pattern,
      '<mark style="background:rgba(250,204,21,.35);color:inherit;padding:0 1px;border-radius:2px;">$1</mark>'
    );
  }

  private jumpToFirstOccurrence(): void {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query || !this.messageRows?.length) return;

    const idx = this.messages.findIndex(m => this.searchTextForMessage(m).includes(query));
    if (idx < 0) return;
    this.messageRows.get(idx)?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private searchTextForMessage(m: Message): string {
    const joined = [
      m.content || '',
      m.rawType || '',
      m.interactive?.title || '',
      m.interactive?.bodyText || '',
      m.interactive?.headerText || '',
      m.interactive?.footerText || '',
      m.interactive?.responseBody || '',
    ].join(' ');
    return joined.toLowerCase();
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
